// @ts-nocheck
// Lead-capture card — funnel leak #2 fix (Shlomo approved 4.8.2026).
//
// Shown inside chapter 1 right after the first exercise, to anonymous
// visitors only. The visitor leaves an email and gets the standard branded
// magic-link email ("קישור הכניסה שלך") — clicking it brings them back
// signed-in, with their reading position and writings synced. So a
// mid-chapter leaver stops being lost-forever and becomes a registered,
// contactable reader. Reuses 100% existing infra (Supabase OTP + Resend
// SMTP + Hebrew templates) — no new tables, no new email pipeline.
//
// Copy is deliberately workshop-language ("מפגש", "סדנה") per the 4.8
// positioning decision: we sell a workshop, not a book.

import { useState } from "react";
import { supabase } from "../lib/supabase";
import { trackLead } from "../lib/fbTrack";

const styles = `
.lead-card { background: linear-gradient(135deg, #FFF8F0 0%, #FFFDF9 100%); border: 1px solid #E8DCC8; border-radius: 16px; padding: 22px 20px; margin: 26px 0 6px; direction: rtl; font-family: 'Heebo', sans-serif; box-shadow: 0 6px 20px rgba(92,61,46,0.08); }
.lead-card-title { font-family: 'Frank Ruhl Libre', serif; font-size: 19px; font-weight: 700; color: #5C3D2E; margin-bottom: 6px; }
.lead-card-body { font-size: 14px; line-height: 1.7; color: #5C3D2E; margin-bottom: 14px; }
.lead-card-row { display: flex; gap: 8px; flex-wrap: wrap; }
.lead-card-input { flex: 1 1 200px; border: 1px solid #D9C8B0; border-radius: 12px; padding: 10px 14px; font-family: 'Heebo', sans-serif; font-size: 14px; color: #2D1B10; background: #fff; direction: ltr; text-align: left; }
.lead-card-input:focus { outline: 2px solid rgba(160,112,74,0.4); }
.lead-card-btn { background: linear-gradient(135deg, #A0704A, #D4875A); color: #fff; border: none; border-radius: 12px; padding: 10px 20px; font-family: 'Heebo', sans-serif; font-size: 14px; font-weight: 600; cursor: pointer; white-space: nowrap; transition: transform .15s; }
.lead-card-btn:hover:not(:disabled) { transform: translateY(-1px); }
.lead-card-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.lead-card-fine { font-size: 12px; color: #8B7355; margin-top: 8px; line-height: 1.6; }
.lead-card-msg { font-size: 14px; line-height: 1.7; padding: 12px 16px; border-radius: 10px; margin-top: 10px; }
.lead-card-msg.ok { background: #E8F4EC; color: #2E7D32; border-right: 3px solid #4CAF50; }
.lead-card-msg.err { background: #FFF0F0; color: #C62828; border-right: 3px solid #E53935; }
`;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

// variant="save": the early "no time right now?" hook for the ~26-second
// ad visitor (29.8 Clarity finding) — same infra, different framing.
export function LeadCaptureCard({ variant = "continue" }: { variant?: "continue" | "save" }) {
  const [email, setEmail] = useState("");
  const [state, setState] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [errMsg, setErrMsg] = useState("");

  const send = async () => {
    const clean = email.trim();
    if (!EMAIL_RE.test(clean)) {
      setErrMsg("כתובת המייל לא נראית תקינה - בדקו אותה ונסו שוב.");
      setState("error");
      return;
    }
    setState("sending");
    // The moment an anonymous reader becomes a contactable lead — same
    // semantic as the AuthModal submit, same pixel event.
    trackLead();
    const { error } = await supabase.auth.signInWithOtp({
      email: clean,
      options: { emailRedirectTo: window.location.origin },
    });
    if (error) {
      setErrMsg(
        /rate/i.test(error.message || "")
          ? "נשלחו יותר מדי בקשות - נסו שוב בעוד דקה."
          : "משהו השתבש בשליחה. נסו שוב עוד רגע."
      );
      setState("error");
      return;
    }
    setState("sent");
  };

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: styles }} />
      <div className="lead-card">
        <div className="lead-card-title">
          {variant === "save"
            ? "⏳ אין לך זמן להמשיך עכשיו?"
            : "🎧 רוצה להמשיך את הסדנה בזמן שלך?"}
        </div>
        {state === "sent" ? (
          <div className="lead-card-msg ok">
            <strong>המייל בדרך אליך!</strong> חפשו הודעה מ-<strong>eiforall</strong> עם
            כותרת "קישור הכניסה שלך" - לחיצה על הכפתור שבה מחזירה אתכם לכאן,
            עם המקום שמור וההקלטות מחכות. (לא רואים? בדקו בספאם / קידומים.)
          </div>
        ) : (
          <>
            <div className="lead-card-body">
              {variant === "save"
                ? "השאירו מייל - ונשלח לכם קישור אישי שיחזיר אתכם בדיוק לכאן, לקריאה או להאזנה, מתי שנוח. בלי סיסמה, בחינם."
                : "השאירו מייל - ונשלח לכם קישור אישי שחוזר בדיוק לנקודה שבה עצרתם, עם המפגש המוקלט בקולו של שלמה. בלי סיסמה, בחינם."}
            </div>
            <div className="lead-card-row">
              <input
                className="lead-card-input"
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") send();
                }}
                aria-label="כתובת מייל להמשך הסדנה"
              />
              <button
                className="lead-card-btn"
                onClick={send}
                disabled={state === "sending"}
              >
                {state === "sending" ? "שולח..." : "שלחו לי את הקישור"}
              </button>
            </div>
            {state === "error" && (
              <div className="lead-card-msg err">{errMsg}</div>
            )}
            <div className="lead-card-fine">
              בלי דואר זבל - רק הקישור שלכם. אפשר להמשיך לקרוא כאן גם בלי
              להשאיר מייל.
            </div>
          </>
        )}
      </div>
    </>
  );
}
